import {
  OvulatoryEventEvidenceLevel,
  OvulatoryEventObservedOutcome,
  OvulatoryEventRuleCyclePhase,
  OvulatoryEventRuleTtcStatus,
  OvulatoryEventState,
} from '../../../common/model/dto/ovulatory-event-prediction/ovulatory-event-prediction';
import {
  OvulatoryEventExcludedReason,
  OvulatoryEventWrongKind,
} from '../../../common/model/type/ovulatory-event-prediction.type';
import Colours from '../../../design-system/colours';

export const OVULATORY_EVENT_STATE_SHORT_LABELS: Record<
  OvulatoryEventState,
  string
> = {
  approaching: 'Approaching',
  not_approaching: 'Not approaching',
  ineligible: 'Ineligible',
};

export const OVULATORY_EVENT_STATE_TEXT_COLOURS: Record<
  OvulatoryEventState,
  string
> = {
  approaching: Colours.OV_ACCENT_GREEN,
  not_approaching: Colours.OV_BASE,
  ineligible: Colours.OV_BASE,
};

const INELIGIBILITY_REASON_LABELS: Record<string, string> = {
  demo_account: 'Demo account',
  missing_day_of_birth: 'No day of birth on the profile',
  reproductive_disorder: 'Reproductive-disorder answers outside model range',
  age_out_of_range: 'Age out of range (20 to 55)',
};

export const getOvulatoryEventIneligibilityReasonLabel = (
  reason: string | null
): string =>
  reason === null
    ? 'No reason recorded'
    : INELIGIBILITY_REASON_LABELS[reason] ?? reason;

export const OVULATORY_EVENT_EVIDENCE_LABELS: Record<
  OvulatoryEventEvidenceLevel,
  string
> = {
  sufficient: 'Sufficient',
  limited: 'Limited',
};

export const OVULATORY_EVENT_NO_EVIDENCE_LABEL = 'None';

export const OVULATORY_EVENT_LIMITED_EVIDENCE_DESCRIPTION =
  'Lower confidence: 3 or fewer days with a PdG reading in the 20 days up to this day.';

export const OVULATORY_EVENT_OUTCOME_LABELS: Record<
  OvulatoryEventObservedOutcome,
  string
> = {
  positive: 'Positive',
  sufficient_negative: 'Sufficient negative',
  unknown: 'Unknown',
  pending: 'Pending',
  event_on_day: 'Event on day',
};

export const OVULATORY_EVENT_OUTCOME_DESCRIPTIONS: Record<
  OvulatoryEventObservedOutcome,
  string
> = {
  positive: 'A PdG-supported ovulatory event began 1 to 5 days later',
  sufficient_negative:
    'No PdG-supported ovulatory event began in the window, with enough follow-up to trust that',
  unknown: 'No event was seen, but follow-up was too sparse to trust that',
  pending: 'Too early to tell, this will change',
  event_on_day: 'A PdG-supported ovulatory event began on this day itself',
};

export const OVULATORY_EVENT_EXCLUDED_LABELS: Record<
  OvulatoryEventExcludedReason,
  string
> = {
  unknown: 'Unknown',
  pending: 'Pending',
  event_on_day: 'Event on day',
  ineligible: 'Ineligible',
};

export const OVULATORY_EVENT_TTC_STATUS_LABELS: Record<
  OvulatoryEventRuleTtcStatus,
  string
> = {
  low: 'Low',
  fertile: 'Fertile',
  high: 'High',
  peak: 'Peak',
  post_ovulation: 'Post rule-detected ovulation',
  period: 'Period',
};

export const OVULATORY_EVENT_CYCLE_PHASE_LABELS: Record<
  OvulatoryEventRuleCyclePhase,
  string
> = {
  menstrual: 'Menstrual',
  follicular: 'Follicular',
  ovulation: 'Rule-detected ovulation',
  luteal: 'Luteal',
};

export const OVULATORY_EVENT_NO_RULE_VALUE_LABEL = 'None';

export const OVULATORY_EVENT_APPROXIMATE_RULE_LABEL =
  '≈ Approximate rule values';

export const OVULATORY_EVENT_APPROXIMATE_RULE_DESCRIPTION =
  'Approximate: this row was scored after its day, so the rule snapshot uses the member data as it was then, not as it was on the day.';

export const OVULATORY_EVENT_WRONG_LABELS: Record<
  OvulatoryEventWrongKind,
  string
> = {
  miss: 'Miss',
  false_alarm: 'False alarm',
};
